import { BoardingMetadata, DaycareMetadata, status as AppointmentStatus } from '@spike/appointment-model';
import { AppointmentOrderItem, CustomOrderItem, Order, OrderItem, OrderizableTypes } from '@spike/order-model';
import moment, { Moment } from 'moment-timezone';
import { BILLING_CALCULATION, BOARDING_DEFAULTS, resolveBillingCalculation } from 'model/Service';

export const LATE_CHECKOUT_FEE_DESCRIPTION = 'Late PickUp Fee';

const EXCLUDED_APPOINTMENT_STATUSES = [
    AppointmentStatus.CANCELLED,
    AppointmentStatus.NO_SHOW,
    AppointmentStatus.DECLINED
];

type LateCheckoutMetadata = (DaycareMetadata | BoardingMetadata) & Record<string, unknown>;
type OrderItemWithOriginalAmount = OrderItem & { originalAmount?: string | null };

export const lateCheckoutAmountsMatch = (left: number, right: number): boolean => {
    return Math.round(left * 100) === Math.round(right * 100);
};

const roundMoney = (value: number): number => {
    return Math.round((value + Number.EPSILON) * 100) / 100;
};

const parseMetadata = (raw: unknown): LateCheckoutMetadata | undefined => {
    if (!raw) return undefined;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? (parsed as LateCheckoutMetadata) : undefined;
        } catch {
            return undefined;
        }
    }
    if (typeof raw === 'object') {
        return raw as LateCheckoutMetadata;
    }
    return undefined;
};

const toOptionalNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
        return Number(value);
    }
    return undefined;
};

const readMetadata = (
    appointmentItem: AppointmentOrderItem
): { businessAreaId?: string; metadata?: LateCheckoutMetadata } => {
    const businessAreaId = appointmentItem.appointment.services[0]?.businessArea?.id;
    const metadata = parseMetadata(appointmentItem.appointment.serviceVariableDuration);
    return { businessAreaId, metadata };
};

const readLatePickupFee = (metadata?: LateCheckoutMetadata) => {
    if (!metadata) return undefined;
    const latePickupFee = (metadata.latePickupFee ?? metadata.late_pickup_fee) as
        | {
              enabled?: boolean;
              type?: string;
              gracePeriod?: number;
              grace_period?: number;
              fees?: Record<string, number>;
          }
        | undefined;
    if (!latePickupFee) return undefined;
    return {
        enabled: !!latePickupFee.enabled,
        type: latePickupFee.type,
        gracePeriod: latePickupFee.gracePeriod ?? latePickupFee.grace_period ?? 0,
        fees: latePickupFee.fees
    };
};

const feeForPet = (fees: Record<string, number> | undefined, petType?: { id?: string; plural?: string }): number => {
    if (!fees) return 0;
    const id = petType?.id?.toLowerCase();
    const plural = petType?.plural?.toLowerCase();
    const raw = (id ? fees[id] : undefined) ?? (plural ? fees[plural] : undefined) ?? 0;
    return Number(raw) || 0;
};

const periodHoursFromDuration = (durationValue?: string): number => {
    if (!durationValue) return 0;
    const match = durationValue.match(/(?<!\d)(?:[2-9]|1[0-2])(?!\d)/);
    return match ? Number(match[0]) : 0;
};

const hoursAfterDeadline = (deadline: Moment, now: Moment): number => {
    const diffMs = now.diff(deadline);
    if (diffMs <= 0) return 0;
    return Math.ceil(moment.duration(diffMs).asHours());
};

const isCalendarDayBilling = (metadata?: LateCheckoutMetadata): boolean => {
    if (!metadata) return false;
    return (
        resolveBillingCalculation(
            metadata.billingCalculation ?? metadata.billing_calculation,
            metadata.dropoff24h ?? metadata.dropoff_24h
        ) === BILLING_CALCULATION.BY_CALENDAR_DAY
    );
};

const parseMoment = (value: unknown, timeZone: string): Moment | undefined => {
    if (!value) return undefined;
    if (moment.isMoment(value)) {
        return value.clone().tz(timeZone);
    }
    const parsed = moment.tz(value as string | Date, timeZone);
    return parsed.isValid() ? parsed : undefined;
};

const readCompletedAt = (appointmentItem: AppointmentOrderItem, timeZone: string): Moment | undefined => {
    const details = appointmentItem.appointment.details as
        | { timeline?: { completedAt?: unknown; completed_at?: unknown } }
        | undefined;
    return parseMoment(details?.timeline?.completedAt ?? details?.timeline?.completed_at, timeZone);
};

const resolveCalendarDayPrice = (appointmentItem: AppointmentOrderItem): number => {
    const appointment = appointmentItem.appointment as typeof appointmentItem.appointment & {
        referencePrice?: number;
        reference_price?: number;
    };
    const referencePrice = toOptionalNumber(appointment.referencePrice ?? appointment.reference_price);
    if (referencePrice && referencePrice > 0) return roundMoney(referencePrice);

    const catalog = Number(appointmentItem.orderItem.originalPrice);
    if (catalog > 0) return roundMoney(catalog);

    const servicePrice = Number(appointmentItem.appointment.services?.[0]?.price);
    if (servicePrice > 0) return roundMoney(servicePrice);

    const nights = Math.max(1, appointmentItem.appointment.nightsCount ?? 1);
    const stay = Number(appointmentItem.orderItem.amount);
    if (stay > 0) return roundMoney(stay / nights);

    return 0;
};

const calculateCalendarDayLateCheckoutFee = (
    appointmentItem: AppointmentOrderItem,
    metadata: LateCheckoutMetadata,
    timeZone: string,
    now: Moment
): number => {
    const scheduledEnd = appointmentItem.appointment.duration?.to;
    if (!scheduledEnd) return 0;

    const completedAt = readCompletedAt(appointmentItem, timeZone);
    const actualCheckout = completedAt ?? now;
    const checkoutDate = (completedAt ?? scheduledEnd).clone().tz(timeZone);
    const cutoff =
        toOptionalNumber(metadata.checkoutCutoffTime ?? metadata.checkout_cutoff_time) ??
        BOARDING_DEFAULTS.CHECKOUT_CUTOFF_TIME;
    const grace =
        toOptionalNumber(metadata.checkoutGracePeriod ?? metadata.checkout_grace_period) ??
        BOARDING_DEFAULTS.CHECKOUT_GRACE_PERIOD;
    const hours = Math.floor(cutoff / 100);
    const minutes = cutoff % 100;
    const deadline = checkoutDate.clone().startOf('day').hour(hours).minute(minutes).second(0).add(grace, 'minutes');

    if (!actualCheckout.isAfter(deadline)) return 0;

    return resolveCalendarDayPrice(appointmentItem);
};

/**
 * Fee for one appointment.
 * SU-2491 automatic charges require the service-level Late Checkout Fee to be enabled.
 * SU-2492 By Calendar Day uses cutoff + grace and does not require that toggle.
 * The booking-level control can still preview a calculated amount when it is off.
 */
export const calculateAppointmentLateCheckoutFee = (
    appointmentItem: AppointmentOrderItem,
    timeZone: string,
    options?: { requireEnabled?: boolean; now?: Moment }
): number => {
    if (EXCLUDED_APPOINTMENT_STATUSES.includes(appointmentItem.appointment.status?.id)) {
        return 0;
    }

    const { businessAreaId, metadata } = readMetadata(appointmentItem);
    if (businessAreaId !== 'daycare' && businessAreaId !== 'boarding') return 0;

    const now = options?.now ?? moment.tz(timeZone);

    if (businessAreaId === 'boarding' && isCalendarDayBilling(metadata)) {
        return metadata ? calculateCalendarDayLateCheckoutFee(appointmentItem, metadata, timeZone, now) : 0;
    }

    const latePickupFee = readLatePickupFee(metadata);
    if (!latePickupFee) return 0;
    if (options?.requireEnabled && !latePickupFee.enabled) return 0;

    const appointmentEnd = appointmentItem.appointment.duration?.to;
    if (!appointmentEnd) return 0;

    const deadline = appointmentEnd
        .clone()
        .tz(timeZone)
        .add(latePickupFee.gracePeriod || 0, 'minutes');
    if (!now.isAfter(deadline)) return 0;

    const price = feeForPet(latePickupFee.fees, appointmentItem.appointment.pet?.type);
    if (!price) return 0;

    const lateHours = hoursAfterDeadline(deadline, now);

    if (businessAreaId === 'daycare') {
        switch (latePickupFee.type) {
            case 'per_hour':
                return price * lateHours;
            case 'additional_half_day':
            case 'additional_full_day': {
                const periodHours = periodHoursFromDuration(
                    (metadata as DaycareMetadata | undefined)?.durationValue ??
                        (typeof metadata?.duration_value === 'string' ? metadata.duration_value : undefined)
                );
                if (!periodHours) return 0;
                const elapsedHours = moment.duration(now.diff(deadline)).asHours();
                return Math.ceil(elapsedHours / periodHours) * price;
            }
            default:
                return 0;
        }
    }

    switch (latePickupFee.type) {
        case 'per_hour':
            return price * lateHours;
        case 'additional_night':
        case 'fixed_fee':
            return price;
        default:
            return 0;
    }
};

export const calculateLateCheckoutFee = (
    appointments: Array<AppointmentOrderItem>,
    timeZone: string,
    options?: { requireEnabled?: boolean; now?: Moment }
): number => {
    const total = appointments.reduce(
        (sum, appointment) => sum + calculateAppointmentLateCheckoutFee(appointment, timeZone, options),
        0
    );
    return roundMoney(total);
};

export const calculateAutomaticLateCheckoutFee = (
    appointments: Array<AppointmentOrderItem>,
    timeZone: string,
    now?: Moment
): number => {
    return calculateLateCheckoutFee(appointments, timeZone, { requireEnabled: true, now });
};

export const findLateCheckoutFeeCustomItem = (order?: Order): CustomOrderItem | undefined => {
    return order?.items.customs?.find(custom => custom.orderItem.description === LATE_CHECKOUT_FEE_DESCRIPTION);
};

export const getLateCheckoutFeeOriginalAmount = (item?: CustomOrderItem): string | undefined => {
    const raw = (item?.orderItem as OrderItemWithOriginalAmount | undefined)?.originalAmount;
    if (raw == null || raw === '') return undefined;
    return String(raw);
};

export const isLateCheckoutFeeOverridden = (item: CustomOrderItem | undefined, automaticAmount: number): boolean => {
    if (!item) return false;
    if (getLateCheckoutFeeOriginalAmount(item) != null) return true;
    return !lateCheckoutAmountsMatch(Number(item.orderItem.amount), automaticAmount);
};

export const buildLateCheckoutFeeOrderItem = (params: {
    order: Order;
    amount: number;
    timeZone: string;
    uuid: string;
    existing?: CustomOrderItem;
    originalAmount?: string | null;
}): OrderItem => {
    const existingItem = params.existing?.orderItem as OrderItemWithOriginalAmount | undefined;
    const orderItem: OrderItemWithOriginalAmount = {
        id: existingItem?.id,
        uuid: existingItem?.uuid ?? params.uuid,
        orderId: params.order.id,
        orderizable: {
            type: OrderizableTypes.CUSTOM
        },
        petId: existingItem?.petId ?? params.order.items.appointments[0]?.appointment?.pet?.id,
        staffId: existingItem?.staffId,
        description: LATE_CHECKOUT_FEE_DESCRIPTION,
        quantity: 1,
        amount: roundMoney(params.amount).toString(),
        discount: existingItem?.discount ?? { amount: '0' },
        startedAt: existingItem?.startedAt ?? moment.tz(params.timeZone),
        originalAmount: params.originalAmount !== undefined ? params.originalAmount : existingItem?.originalAmount
    };
    return orderItem;
};
